<template>
  <div class="fa-page">
    <!-- ==================== 概览头 ==================== -->
    <div class="fa-hero">
      <div class="fa-hero-l">
        <div class="fa-hero-title">
          <el-button icon="ArrowLeft" text @click="go({ path: '/formList' })">返回</el-button>
          <h2>审批管理</h2>
          <span class="fa-hero-badge">审批流</span>
        </div>
        <p class="fa-hero-sub">
          审批是表单级可选项，未开启审批的表单不会产生审批单；
          这里按提交人 / 表单 / 状态 / 日期查看审批进度与流水。
          抄送者只「可见」不「可操作」——历史环节审批者同样可见但不可操作。
          家长端的「抄送我的」在「我的审批」里查看（后台列表暂不按抄送人筛选）。
        </p>
        <div class="fa-ov">
          <div class="fa-ov-item">
            <b class="c-ing">{{ ovText(ov.ing) }}</b><span>审批中</span>
          </div>
          <div class="fa-ov-item">
            <b class="c-pass">{{ ovText(ov.pass) }}</b><span>已通过</span>
          </div>
          <div class="fa-ov-item">
            <b class="c-reject">{{ ovText(ov.reject) }}</b><span>已驳回</span>
          </div>
          <div class="fa-ov-item">
            <b class="c-withdraw">{{ ovText(ov.withdraw) }}</b><span>已撤回</span>
          </div>
        </div>
      </div>
      <div class="fa-hero-r">
        <el-button icon="Refresh" @click="refresh">刷 新</el-button>
      </div>
    </div>

    <!-- ==================== 筛选条 ==================== -->
    <div class="fa-bar">
      <el-select v-model="query.formId" clearable filterable placeholder="全部表单" class="fa-sel-form">
        <el-option v-for="f in formOptions" :key="f.id" :label="f.title" :value="f.id"></el-option>
      </el-select>
      <el-select v-model="query.status" clearable placeholder="全部状态" class="fa-sel-status">
        <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value"></el-option>
      </el-select>
      <el-input
          v-model="query.keyword"
          clearable
          placeholder="提交人姓名"
          prefix-icon="Search"
          class="fa-keyword"
          @keyup.enter="search"></el-input>
      <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="YYYY-MM-DD"
          range-separator="~"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          class="fa-date"></el-date-picker>
      <el-button type="primary" icon="Search" @click="search">查询</el-button>
      <el-button icon="RefreshLeft" @click="reset">重置</el-button>
      <span class="fa-count">共 {{ pageTotal }} 条</span>
    </div>

    <!-- ==================== 表格 ==================== -->
    <div class="fa-card">
      <el-table
          v-loading="listLoading"
          :data="tableData"
          :cell-style="{padding: '6px 0'}"
          row-key="id"
          class="fa-table">
        <el-table-column min-width="180" show-overflow-tooltip label="表单">
          <template #default="scope">{{ scope.row.formTitle || '—' }}</template>
        </el-table-column>
        <el-table-column width="120" label="提交人">
          <template #default="scope">{{ scope.row.submitterName || '—' }}</template>
        </el-table-column>
        <el-table-column min-width="220" label="审批进度">
          <template #default="scope">
            <div class="fa-progress">
              <div class="fa-progress-top">
                <span class="fa-progress-step">第 {{ scope.row.currentStep || 0 }}/{{ scope.row.totalStep || 0 }} 级</span>
                <span v-if="scope.row.currentStepName" class="fa-progress-name">{{ scope.row.currentStepName }}</span>
              </div>
              <div class="fa-prog">
                <span :style="{ width: stepPercent(scope.row), background: stepColor(scope.row) }"></span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="90" align="center" label="状态">
          <template #default="scope">
            <el-tag :type="approvalTagType(scope.row.status)" size="small">
              {{ approvalText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column width="120" align="center" label="抄送">
          <template #default="scope">
            <el-tag v-if="ccIdsOf(scope.row).length" size="small" effect="plain"
                    :title="'抄送人 userId：' + ccIdsOf(scope.row).join('、')">
              抄送 {{ ccIdsOf(scope.row).length }} 人
            </el-tag>
            <span v-else class="fa-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column width="160" label="提交时间">
          <template #default="scope">{{ scope.row.createdAt || '—' }}</template>
        </el-table-column>
        <el-table-column width="160" label="结束时间">
          <template #default="scope">{{ scope.row.finishedAt || '—' }}</template>
        </el-table-column>
        <el-table-column width="100" align="center" fixed="right" label="操作">
          <template #default="scope">
            <el-button size="small" text type="primary" @click="openDetail(scope.row)">查看</el-button>
          </template>
        </el-table-column>

        <!-- 空态 / 失败态分开：失败给「重试」，不留一个点了没反应的按钮 -->
        <template #empty>
          <div v-if="listFailed" class="fa-fail">
            <el-icon><WarningFilled /></el-icon>
            <p>{{ listError || '审批列表加载失败' }}</p>
            <el-button type="primary" icon="RefreshRight" @click="selectList">重试</el-button>
          </div>
          <div v-else class="fa-empty">
            <el-icon><Checked /></el-icon>
            <p>{{ emptyText }}</p>
            <el-button v-if="hasFilter" text type="primary" @click="reset">重置筛选</el-button>
          </div>
        </template>
      </el-table>
    </div>

    <div class="pagination">
      <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :page-sizes="[10, 20, 50, 100]"
          :current-page="query.pageIndex"
          :page-size="query.pageSize"
          :total="pageTotal"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"></el-pagination>
    </div>

    <!-- ==================== 详情抽屉 ==================== -->
    <el-drawer v-model="detailVisible" title="审批详情" size="580px">
      <div v-loading="detailLoading" class="fa-detail">
        <!-- 详情失败态：抽屉里也能重试，不留空白 -->
        <div v-if="detailFailed" class="fa-fail fa-fail--drawer">
          <el-icon><WarningFilled /></el-icon>
          <p>{{ detailError || '审批详情加载失败' }}</p>
          <el-button type="primary" icon="RefreshRight" @click="loadDetail">重试</el-button>
        </div>

        <template v-else>
          <!-- 上部：基本信息 -->
          <div class="fa-detail-head">
            <div class="fa-detail-title">{{ detailInstance.formTitle || '—' }}</div>
            <div class="fa-meta">
              <div class="fa-meta-item">
                <span class="fa-meta-k">提交人</span>
                <span>{{ detailInstance.submitterName || '—' }}</span>
              </div>
              <div class="fa-meta-item">
                <span class="fa-meta-k">状态</span>
                <el-tag :type="approvalTagType(detailInstance.status)" size="small">
                  {{ approvalText(detailInstance.status) }}
                </el-tag>
              </div>
              <div class="fa-meta-item">
                <span class="fa-meta-k">当前环节</span>
                <span>{{ detailInstance.currentStepName || '—' }}</span>
              </div>
              <div class="fa-meta-item">
                <span class="fa-meta-k">进度</span>
                <span>第 {{ detailInstance.currentStep || 0 }}/{{ detailInstance.totalStep || 0 }} 级</span>
              </div>
              <div class="fa-meta-item">
                <span class="fa-meta-k">操作权限</span>
                <el-tag v-if="detailInstance.canApprove" type="success" size="small">可操作（当前环节审批人）</el-tag>
                <el-tag v-else type="info" size="small">仅可见（非当前环节审批人）</el-tag>
              </div>
              <div class="fa-meta-item">
                <span class="fa-meta-k">抄送人</span>
                <span v-if="ccIdsOf(detailInstance).length">抄送 {{ ccIdsOf(detailInstance).length }} 人（userId：{{ ccIdsOf(detailInstance).join('、') }}）</span>
                <span v-else class="fa-muted">无</span>
              </div>
              <div class="fa-meta-item">
                <span class="fa-meta-k">提交时间</span>
                <span>{{ detailInstance.createdAt || '—' }}</span>
              </div>
              <div class="fa-meta-item">
                <span class="fa-meta-k">结束时间</span>
                <span>{{ detailInstance.finishedAt || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- 中部：审批链 -->
          <el-divider content-position="left">审批链</el-divider>
          <div v-if="steps.length" class="fa-chain">
            <el-steps
                :active="chainActive"
                :finish-status="chainFinishStatus"
                :process-status="chainProcessStatus"
                align-center>
              <el-step
                  v-for="s in steps"
                  :key="s.step"
                  :title="'第 ' + s.step + ' 级 · ' + (s.name || '审批')"
                  :description="s.mode === 'all' ? '需全部审批人通过' : '任一人通过即可'"></el-step>
            </el-steps>
          </div>
          <div v-else class="fa-none">暂无审批链配置</div>

          <!-- 下部：时间轴 -->
          <el-divider content-position="left">审批流水</el-divider>
          <div v-if="logs.length" class="fa-timeline">
            <el-timeline>
              <el-timeline-item
                  v-for="log in logs"
                  :key="log.id"
                  :type="actionType(log.action)"
                  :timestamp="log.createdAt || ''"
                  placement="top">
                <div class="fa-log">
                  <div class="fa-log-head">
                    <span class="fa-log-action" :class="'act-' + (log.action || 'unknown')">
                      {{ actionText(log.action) }}
                    </span>
                    <span class="fa-log-step">{{ log.stepName || ('第 ' + (log.step || 0) + ' 级') }}</span>
                    <span class="fa-log-op">{{ log.operatorName || '系统' }}</span>
                  </div>
                  <div v-if="log.comment" class="fa-log-comment">{{ log.comment }}</div>
                </div>
              </el-timeline-item>
            </el-timeline>
          </div>
          <div v-else class="fa-none">暂无审批流水</div>
        </template>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import {
  formPage,
  formApprovalPage,
  formApprovalDetail,
  APPROVAL_STATUS_TEXT,
  APPROVAL_STATUS_TYPE,
} from '@/api/form';

// 与后端 XfFormApprovalLog 常量一一对应（不要凭猜）：
// approve 通过 / reject 驳回 / transfer 转交 / withdraw 撤回 / submit 提交（含驳回后重提）
const ACTION_TEXT = {
  approve: '通过',
  reject: '驳回',
  transfer: '转交',
  withdraw: '撤回',
  submit: '提交',
};

export default {
  name: 'FormApproval',
  data() {
    return {
      listLoading: false,
      listFailed: false,
      listError: '',
      tableData: [],
      pageTotal: 0,
      formOptions: [],
      // 概览：4 个状态分别取一次 total；取不到时置 null → 显示「—」
      ov: { ing: null, pass: null, reject: null, withdraw: null },
      dateRange: [],
      query: {
        pageIndex: 1,
        pageSize: 10,
        formId: null,
        status: null,
        startDate: '',
        endDate: '',
        keyword: '',
      },
      // 详情抽屉
      detailVisible: false,
      detailLoading: false,
      detailFailed: false,
      detailError: '',
      detailId: null,
      detailInstance: {},
      steps: [],
      logs: [],
    };
  },
  computed: {
    statusOptions() {
      return Object.keys(APPROVAL_STATUS_TEXT).map(k => ({
        value: Number(k),
        label: APPROVAL_STATUS_TEXT[k],
      }));
    },
    hasFilter() {
      return this.query.formId != null
          || this.query.status != null
          || !!this.query.keyword
          || !!this.query.startDate
          || !!this.query.endDate;
    },
    emptyText() {
      if (this.hasFilter) return '没有符合筛选条件的审批单';
      return '还没有审批单 —— 审批是表单级可选项，未开启审批的表单不会产生审批单';
    },
    /** 审批链高亮：已通过 = 全绿到底；驳回 = 停在被驳回环节（红） */
    chainActive() {
      const total = Number(this.detailInstance.totalStep) || this.steps.length;
      const cur = Number(this.detailInstance.currentStep) || 0;
      if (this.detailInstance.status === 2) return total;
      return Math.max(0, cur - 1);
    },
    chainFinishStatus() {
      return 'success';
    },
    chainProcessStatus() {
      if (this.detailInstance.status === 2) return 'success';
      if (this.detailInstance.status === 3) return 'error';
      return 'process';
    },
  },
  methods: {
    /* ---------------- 展示口径 ---------------- */
    approvalText(status) {
      return APPROVAL_STATUS_TEXT[status] || '未知';
    },
    approvalTagType(status) {
      return APPROVAL_STATUS_TYPE[status] || 'info';
    },
    /** 概览数字：null 显示「—」，不要用 0 冒充 */
    ovText(v) {
      return (v === null || v === undefined) ? '—' : v;
    },
    stepPercent(row) {
      const total = Number(row.totalStep) || 0;
      const cur = Number(row.currentStep) || 0;
      if (total <= 0) return 0;
      return Math.min(100, Math.round((cur / total) * 100));
    },
    stepColor(row) {
      if (row.status === 2) return 'var(--el-color-success, #67c23a)';
      if (row.status === 3) return 'var(--el-color-danger, #f56c6c)';
      if (row.status === 4) return 'var(--yh-t5, #c0c4cc)';
      return 'var(--yh-theme, #8dc63f)';
    },
    actionText(action) {
      return ACTION_TEXT[action] || (action || '未知');
    },
    /**
     * ★ v3.17：抄送人 userId 快照（ccIds）解析。
     * 后端落库是逗号串（varchar），这里统一成数组；空串 / 脏值一律过滤，避免出现「抄送 人」。
     */
    ccIdsOf(row) {
      if (!row) return [];
      const raw = row.ccIds;
      if (Array.isArray(raw)) return raw.map(x => String(x)).filter(x => x !== '');
      if (typeof raw !== 'string' || !raw) return [];
      return raw.split(',').map(s => s.trim()).filter(s => s !== '');
    },
    /** el-timeline-item 的 type（Element Plus：primary/success/warning/danger/info） */
    actionType(action) {
      if (action === 'approve') return 'success';
      if (action === 'reject') return 'danger';
      if (action === 'transfer') return 'warning';
      if (action === 'withdraw') return 'info';
      if (action === 'submit') return 'primary';
      return 'info';
    },
    /* ---------------- 数据 ---------------- */
    loadFormOptions() {
      formPage({ pageIndex: 1, pageSize: 200, keyword: '' }).then(val => {
        if (val && val.code === 200) {
          const records = (val.data && val.data.records) || [];
          this.formOptions = records.map(r => ({ id: r.id, title: r.title || ('#' + r.id) }));
        }
      }).catch(() => { /* 下拉拉取失败不影响主列表 */ });
    },
    /**
     * 概览：不使用当前页数据累加（那只会算出一页的数字、骗人）。
     * 用同一个分页接口，pageSize:1，分别带 status=1/2/3/4 各取一次 total，并发 4 次。
     * 任一次非 200 / 抛错 → 对应数字置 null，页面显示「—」。
     * 口径：跟随「表单 / 提交人 / 日期」筛选，但**不受状态筛选影响**（否则四个数字会互相塌陷）。
     */
    loadOverview() {
      this.syncDateRange();
      const base = { pageIndex: 1, pageSize: 1 };
      if (this.query.formId != null) base.formId = this.query.formId;
      if (this.query.keyword) base.keyword = this.query.keyword;
      if (this.query.startDate) base.startDate = this.query.startDate;
      if (this.query.endDate) base.endDate = this.query.endDate;
      const one = (status) => {
        const param = Object.assign({}, base, { status });
        return formApprovalPage(param).then(val => {
          if (val && val.code === 200 && val.data) return Number(val.data.total || 0);
          return null;
        }).catch(() => null);
      };
      Promise.all([one(1), one(2), one(3), one(4)]).then(([ing, pass, reject, withdraw]) => {
        this.ov = { ing, pass, reject, withdraw };
      });
    },
    syncDateRange() {
      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        this.query.startDate = this.dateRange[0];
        this.query.endDate = this.dateRange[1];
      } else {
        this.query.startDate = '';
        this.query.endDate = '';
      }
    },
    selectList() {
      this.syncDateRange();
      this.listLoading = true;
      this.listFailed = false;
      this.listError = '';
      formApprovalPage(this.query).then(val => {
        this.listLoading = false;
        if (val && val.code === 200) {
          this.tableData = (val.data && val.data.records) || [];
          this.pageTotal = (val.data && val.data.total) || 0;
        } else {
          this.tableData = [];
          this.pageTotal = 0;
          this.listFailed = true;
          this.listError = (val && val.message) || '审批列表加载失败';
        }
      }).catch((err) => {
        this.listLoading = false;
        this.tableData = [];
        this.pageTotal = 0;
        this.listFailed = true;
        this.listError = (err && err.message) || '审批列表加载失败，请检查网络';
      });
    },
    refresh() {
      this.selectList();
      this.loadOverview();
    },
    search() {
      this.query.pageIndex = 1;
      this.refresh();
    },
    reset() {
      this.query.formId = null;
      this.query.status = null;
      this.query.keyword = '';
      this.dateRange = [];
      this.syncDateRange();
      this.query.pageIndex = 1;
      this.refresh();
    },
    handleSizeChange(size) {
      this.query.pageSize = size;
      this.query.pageIndex = 1;
      this.selectList();
    },
    handlePageChange(index) {
      this.query.pageIndex = index;
      this.selectList();
    },
    /* ---------------- 详情 ---------------- */
    openDetail(row) {
      this.detailId = row.id;
      this.detailVisible = true;
      this.loadDetail();
    },
    loadDetail() {
      if (this.detailId == null) return;
      this.detailLoading = true;
      this.detailFailed = false;
      this.detailError = '';
      formApprovalDetail(this.detailId).then(val => {
        this.detailLoading = false;
        if (val && val.code === 200 && val.data) {
          const d = val.data;
          this.detailInstance = d.instance || {};
          this.steps = Array.isArray(d.steps) ? d.steps : [];
          this.logs = Array.isArray(d.logs) ? d.logs : [];
        } else {
          this.detailInstance = {};
          this.steps = [];
          this.logs = [];
          this.detailFailed = true;
          this.detailError = (val && val.message) || '审批详情加载失败';
        }
      }).catch((err) => {
        this.detailLoading = false;
        this.detailInstance = {};
        this.steps = [];
        this.logs = [];
        this.detailFailed = true;
        this.detailError = (err && err.message) || '审批详情加载失败，请检查网络';
      });
    },
    /* ---------------- 通用 ---------------- */
    /** 与 FormList 同款安全跳转：push 失败要弹原因，不静默 */
    go(target) {
      const p = this.$router.push(target);
      if (p && typeof p.catch === 'function') {
        p.catch(err => {
          const msg = (err && err.message) || '未知错误';
          this.$message.error(`打开页面失败：${msg}（若是改版后首次进入，请按 Ctrl+F5 强制刷新）`);
        });
      }
    },
  },
  created() {
    // 支持从列表页跳转带 formId 进来；没带也不拦——表单选择器常驻在筛选条里，不是死胡同
    const q = this.$route.query.formId;
    this.query.formId = (q != null && q !== '' && !Number.isNaN(Number(q))) ? Number(q) : null;
    this.loadFormOptions();
    this.selectList();
    this.loadOverview();
  },
};
</script>

<style scoped>

/* @form-tokens:start */
/* ======================= 表单模块设计令牌（v3.11 视觉统一）=======================
 * ★ 本块在多个表单页各存一份（CSS 变量按作用域隔离，无法跨 scoped 共享）。
 *   改配色的唯一入口 = D:/tmp/shot/form_tokens.py 的 TOKEN_BLOCK，改完重跑该脚本
 *   一键同步；**不要手改单页**，否则多份会漂移。
 * ★ 为什么不放全局 :root：--el-color-primary 一旦全局生效会波及后台所有模块
 *   （订单 / 教练 / 财务…）。本模块只对「表单」相关页负责，故挂在页面根容器上，
 *   作用域精确、零外溢。
 * ========================================================================== */
.fa-page {
  --el-color-primary: #8dc63f;
  --el-color-primary-light-3: #afd779;
  --el-color-primary-light-5: #c6e39f;
  --el-color-primary-light-7: #ddeec5;
  --el-color-primary-light-8: #e8f4d9;
  --el-color-primary-light-9: #f4f9ec;
  --el-color-primary-dark-2: #719e32;

  /* —— 文字五档（严格对齐 Element 灰阶） —— */
  --yh-t1: #303133; /* 主文字 */
  --yh-t2: #606266; /* 常规 */
  --yh-t3: #909399; /* 次要 */
  --yh-t4: #a8abb2; /* 占位 */
  --yh-t5: #c0c4cc; /* 禁用 */

  /* —— 主色四档 —— */
  --yh-theme: #8dc63f;
  --yh-theme-d: #7cad37;
  --yh-theme-dd: #5f9427;
  --yh-theme-l: #eef7e2;
  --yh-theme-ll: #f6fbef;

  /* —— 面与线 —— */
  --yh-bg: #f5f7fa;
  --yh-fill: #fafbfc;
  --yh-line: #ebeef5;
  --yh-line-d: #dcdfe6;
  --yh-divide: #f2f3f5;

  /* —— 圆角五档 —— */
  --yh-r-xs: 4px;
  --yh-r-sm: 6px;
  --yh-r: 8px;
  --yh-r-lg: 12px;
  --yh-r-pill: 999px;
}
/* @form-tokens:end */

.fa-page {
  padding: 16px 20px 30px;
  min-height: 100%;
  background-color: #f5f7fa;
  box-sizing: border-box;
}

/* ==================== 概览头 ==================== */
.fa-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 16px 20px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fa-hero-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fa-hero-title h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fa-hero-badge {
  padding: 1px 8px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
  font-size: 12px;
}

.fa-hero-sub {
  margin: 6px 0 0;
  max-width: 760px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t3);
}

.fa-ov {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.fa-ov-item {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 4px 10px;
  border-radius: var(--yh-r);
  background: var(--yh-fill);
}

.fa-ov-item b {
  font-size: 15px;
  color: var(--yh-t1);
}

.fa-ov-item span {
  font-size: 12px;
  color: var(--yh-t3);
}

.c-ing { color: #e6a23c !important; }
.c-pass { color: #67c23a !important; }
.c-reject { color: #f56c6c !important; }
.c-withdraw { color: var(--yh-t3) !important; }

.fa-hero-r {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 0 0 auto;
}

/* ==================== 筛选条 ==================== */
.fa-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 12px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fa-sel-form {
  width: 200px;
}

.fa-sel-status {
  width: 130px;
}

.fa-keyword {
  width: 170px;
}

.fa-date {
  width: 260px;
}

.fa-count {
  margin-left: auto;
  font-size: 12px;
  color: var(--yh-t3);
}

/* ==================== 表格卡 ==================== */
.fa-card {
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 6px 12px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fa-table :deep(.el-table__inner-wrapper::before) {
  display: none;
}

.fa-table :deep(.el-table__header th) {
  background: var(--yh-fill);
  color: var(--yh-t2);
  font-weight: 600;
}

.fa-table :deep(.el-table__row:hover > td) {
  background: var(--yh-theme-ll) !important;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

/* 进度 */
.fa-progress-top {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.fa-progress-step {
  font-size: 13px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fa-progress-name {
  font-size: 12px;
  color: var(--yh-t3);
}

.fa-prog {
  height: 5px;
  margin-top: 5px;
  border-radius: var(--yh-r-pill);
  background: #f0f1f3;
  overflow: hidden;
}

.fa-prog span {
  display: block;
  height: 100%;
  border-radius: var(--yh-r-pill);
  transition: width 0.3s;
}

/* 空态 / 失败态 */
.fa-empty,
.fa-fail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 26px 0 12px;
}

.fa-empty .el-icon,
.fa-fail .el-icon {
  font-size: 32px;
  color: #dcdfe6;
}

.fa-fail .el-icon {
  color: #e6a23c;
}

.fa-empty p,
.fa-fail p {
  margin: 0;
  font-size: 13px;
  color: var(--yh-t4);
}

.fa-fail--drawer {
  padding: 60px 0;
}

/* ==================== 详情抽屉 ==================== */
.fa-detail {
  padding: 0 4px;
  min-height: 200px;
}

.fa-detail-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--yh-t1);
  margin-bottom: 10px;
}

.fa-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;
}

.fa-meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--yh-t1);
}

.fa-meta-k {
  flex: 0 0 62px;
  color: var(--yh-t3);
}

.fa-muted {
  color: var(--yh-t4);
}

.fa-chain {
  padding: 6px 0 4px;
}

.fa-none {
  padding: 14px 0;
  font-size: 13px;
  color: var(--yh-t4);
}

.fa-timeline {
  padding-left: 2px;
}

.fa-log-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.fa-log-action {
  font-weight: 600;
  color: var(--yh-t1);
}

.act-approve { color: #67c23a; }
.act-reject { color: #f56c6c; }
.act-transfer { color: #e6a23c; }
.act-withdraw { color: var(--yh-t3); }
.act-submit { color: var(--yh-theme-dd); }

.fa-log-step {
  font-size: 12px;
  color: var(--yh-t3);
}

.fa-log-op {
  font-size: 12px;
  color: var(--yh-t2);
}

.fa-log-comment {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.7;
  color: var(--yh-t2);
}
</style>
